import {useCallback, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {CPaymentLinkStatuses} from '@/app/main/paymentLink/models/constants';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {useLoading} from '@/app/zustand/useLoading';
import {PAYMENT_LINK_ERROR_MAP} from '@/dataSource/src/api/payment-link/constants';
import {
  mockPaymentLinkFees,
  mockPaymentLinkFeeVenmo,
} from '@/dataSource/src/api/payment-link/mock';
import {ErrorManager} from '@/utils/errors/errorManager';
import logError from '@/utils/errors/logError';

import {
  useLazyGetCheckoutResultQuery,
  useLazyGetFeesQuery,
  useLazyGetPaymentLinkQuery,
  usePostLinkCheckoutMutation,
} from './api';
import {
  CTypeOptionCheckout,
  FeeModel,
  ICheckoutResultResponseModel,
  IFee,
  IPublicRequestCheckoutUrl,
  PaymentLinkModel,
} from './entities';
import {
  getCheckoutResultMapper,
  getFeesMapper,
  getPaymentLinkMapper,
} from './mapper';
import {usePaymentLinkTrackers} from './trackers';
import {transformPaymentLinkInformationWithError} from './utils/errorMapper';

const fileName = 'useRepository Payment link';

export const usePaymentLinkRepository = (paymentLinkId?: string) => {
  const {t} = useTranslation();
  const [
    triggerGetPaymentLink,
    {isFetching: isFetchingPaymentLink, error: paymentLinkError},
  ] = useLazyGetPaymentLinkQuery();
  const [triggerGetFees, {isFetching: isFetchingGetFees}] =
    useLazyGetFeesQuery();

  const [triggerGetCheckoutResult, {isFetching: isFetchingGetCheckoutResult}] =
    useLazyGetCheckoutResultQuery();

  const [triggerPostLinkCheckout] = usePostLinkCheckoutMutation();

  const {startLoading, stopLoading} = useLoading();

  const [paymentLinkData, setPaymentLinkData] = useState<
    PaymentLinkModel | undefined
  >(undefined);

  const [paymentLinkErrorCode, setPaymentLinkErrorCode] = useState<string>('');

  const [feesData, setFeesData] = useState<FeeModel | undefined>(undefined);
  const [checkoutResultData, setCheckoutResultData] = useState<
    ICheckoutResultResponseModel | undefined
  >(undefined);
  const {
    trackApiPaymentLinkGetInformationSuccess,
    trackApiPaymentLinkGetInformationError,
    trackViewRequestMoneyPaymentRequest,
    trackViewRequestMoneyLinkExpired,
    trackApiCheckoutFeesSuccess,
    trackApiRequestMoneyPaymentOutcomeSuccess,
    trackApiRequestMoneyPaymentError,
    trackApiPaymentLinkCreatePublicCheckoutLinkSuccess,
    trackApiPaymentLinkCreatePublicCheckoutLinkError,
    trackViewRequestMoneyInvalidLink,
  } = usePaymentLinkTrackers();

  const checkStatusAndTrackEvents = useCallback(
    response => {
      switch (response.status) {
        case CPaymentLinkStatuses.VALID:
          trackViewRequestMoneyPaymentRequest(response);
          break;
        case CPaymentLinkStatuses.USED:
          trackViewRequestMoneyLinkExpired(response);
          break;
        default:
          break;
      }
    },
    [trackViewRequestMoneyPaymentRequest, trackViewRequestMoneyLinkExpired],
  );

  const fetchPaymentLink = useCallback(async () => {
    startLoading();
    if (!paymentLinkId) {
      stopLoading();
      return;
    }
    try {
      const result = await triggerGetPaymentLink(paymentLinkId);
      if (!result) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: t('payment_link.no_data.title'),
          message: t('payment_link.no_data.description'),
        });
        throw new Error('No response received from triggerGetPaymentLink');
      }

      if ('error' in result && result.error) {
        throw result.error;
      }

      if (!result.data) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: t('payment_link.no_data.title'),
          message: t('payment_link.no_data.description'),
        });
        logError(new Error('No data found loading payment link. WAF 202?'), {
          customTitle: 'Error fetching payment link without data',
          fileName: fileName,
          functionName: 'fetchPaymentLink',
          extraTags: {
            payment_link_id: paymentLinkId,
          },
        });
      }
      trackApiPaymentLinkGetInformationSuccess();
      const mappedData = getPaymentLinkMapper(result.data);
      checkStatusAndTrackEvents(mappedData);
      setPaymentLinkData(mappedData);
    } catch (error) {
      trackApiPaymentLinkGetInformationError(error);
      if (error?.data?.errors?.length > 0)
        setPaymentLinkErrorCode(error?.data?.errors?.[0]?.code);
      logError(error, {
        customTitle: 'Error fetching payment link',
        fileName: fileName,
        functionName: 'fetchPaymentLink',
        extraTags: {
          payment_link_id: paymentLinkId,
        },
      });
      const paymentInfo = transformPaymentLinkInformationWithError(
        paymentLinkId,
        error,
      );

      if (paymentInfo.status === CPaymentLinkStatuses.NOT_VALID) {
        trackViewRequestMoneyInvalidLink(paymentInfo);
      }
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentLinkId, startLoading, stopLoading, triggerGetPaymentLink]);

  const fetchFees = useCallback(async () => {
    try {
      startLoading();
      const result = await triggerGetFees();
      if (!result) {
        throw new Error('No response received from triggerGetFees');
      }

      if ('error' in result && result.error) {
        throw result.error;
      }

      if (!result.data) {
        logError(new Error('No data found loading fees. WAF 202?'), {
          customTitle: 'Error fetching fees without data',
          fileName: fileName,
          functionName: 'fetchFees',
          extraTags: {
            payment_link_id: paymentLinkId,
          },
        });
      }

      trackApiCheckoutFeesSuccess();
      const mappedData = getFeesMapper(result.data);
      const fees = Array.isArray(mappedData?.fees)
        ? [...mappedData.fees]
        : null;
      let updatedFees: IFee[];

      if (fees) {
        const venmoIndex =
          fees.findIndex(fee => fee.type === CTypeOptionCheckout.DEBIT_CARD) +
          1;

        fees.splice(venmoIndex, 0, mockPaymentLinkFeeVenmo);
        updatedFees = fees;
      } else {
        updatedFees = [...mockPaymentLinkFees];
      }

      setFeesData({fees: updatedFees});
    } catch (error) {
      ErrorManager.handleError(error);
      logError(error, {
        customTitle: 'Error fetching fees',
        fileName: fileName,
        functionName: 'fetchFees',
        extraTags: {
          payment_link_id: paymentLinkId,
        },
      });
      setFeesData({
        fees: mockPaymentLinkFees,
      });
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLoading, stopLoading, triggerGetFees]);

  const handleGetCheckoutResult = useCallback(
    async (checkoutId: string) => {
      try {
        const result = await triggerGetCheckoutResult(checkoutId);
        if (!result) {
          throw new Error('No response received from triggerGetCheckoutResult');
        }

        if ('error' in result && result.error) {
          throw result.error;
        }

        if (result.data?.amount) {
          trackApiRequestMoneyPaymentOutcomeSuccess(checkoutId, result.data);
          const mappedData = getCheckoutResultMapper(result.data);
          setCheckoutResultData(mappedData);
        } else {
          throw new Error(
            'Invalid response from getCheckoutResult: amount is missing',
          );
        }
      } catch (error) {
        trackApiRequestMoneyPaymentError(error, checkoutId);
        logError(error, {
          customTitle: 'Error fetching checkout result',
          fileName: fileName,
          functionName: 'handleGetCheckoutResult',
          data: {
            checkoutId: checkoutId,
          },
        });
        setCheckoutResultData({
          status: 'error',
          amount: 0,
          amount_without_fee: 0,
          created_at: '',
          type: '',
          receiver_user: undefined,
        });
      }
    },
    [
      trackApiRequestMoneyPaymentError,
      trackApiRequestMoneyPaymentOutcomeSuccess,
      triggerGetCheckoutResult,
    ],
  );

  const handlePostCheckoutLink = async (body: IPublicRequestCheckoutUrl) => {
    try {
      startLoading();
      const response = await triggerPostLinkCheckout(body);
      if (!response) {
        throw new Error('No response received from triggerPostLinkCheckout');
      }

      if ('error' in response && response.error) {
        throw response.error;
      }

      if (!response.data) {
        throw new Error(
          'No data found in response from triggerPostLinkCheckout',
        );
      }

      trackApiPaymentLinkCreatePublicCheckoutLinkSuccess(
        body,
        response.data.link,
      );
      return response.data;
    } catch (error) {
      ErrorManager.handleError(error, {
        customErrorMap: PAYMENT_LINK_ERROR_MAP,
      });
      trackApiPaymentLinkCreatePublicCheckoutLinkError(error);
      logError(error, {
        customTitle: 'Error creating public checkout link',
        fileName: fileName,
        functionName: 'handlePostCheckoutLink',
        extraTags: {
          payment_link_id: body.request_money_id,
        },
      });
    } finally {
      stopLoading();
    }
  };

  return {
    paymentLinkData,
    isFetchingPaymentLink,
    fetchPaymentLink,
    paymentLinkError,
    fetchFees,
    feesData,
    isFetchingGetFees,
    handlePostCheckoutLink,
    handleGetCheckoutResult,
    checkoutResultData,
    isFetchingGetCheckoutResult,
    paymentLinkErrorCode,
  };
};
